import { AppEnvironment } from './app-environment';

/** Console-style log level name a module can be configured to log at,
 * independent of any specific library's own log-level enum. */
export type AppLogLevel = 'error' | 'warn' | 'info' | 'debug' | 'trace';

declare global {
  interface Window {
    /** Per-module diagnostic log level. Read via `AppLogging.get`, set via
     * `AppLogging.setLevel` -- do not assign to this directly, since that
     * bypasses persistence. */
    appLogging: Record<string, AppLogLevel>;
  }
}

/** Per-module diagnostic log levels (e.g. `AppLogging.setLevel('auth', 'debug')`
 * from devtools). Levels persist across reloads via localStorage, seeded from
 * each environment's `defaultLogLevels` on load. */
export class AppLogging {
  private static readonly STORAGE_KEY = 'appLogging';

  static {
    window.appLogging = {
      ...AppEnvironment.defaultLogLevels,
      ...AppLogging.readPersistedLevels(),
      ...window.appLogging,
    };
  }

  static get(name: string): AppLogLevel | undefined {
    return window.appLogging[name];
  }

  static setLevel(name: string, level: AppLogLevel): void {
    window.appLogging[name] = level;
    AppLogging.persist();
  }

  private static readPersistedLevels(): Record<string, AppLogLevel> {
    try {
      const raw = localStorage.getItem(AppLogging.STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Record<string, AppLogLevel>) : {};
    } catch {
      return {};
    }
  }

  private static persist(): void {
    try {
      localStorage.setItem(AppLogging.STORAGE_KEY, JSON.stringify(window.appLogging));
    } catch {
      // Storage unavailable (e.g. private browsing quota) -- the in-memory
      // value set above still applies for the rest of this page load.
    }
  }
}
